/**
 * Телефон в форме мастера (5-М8).
 *
 * ЗДЕСЬ ЖИВЁТ ГРАНИЦА, РАДИ КОТОРОЙ ФОРМА МАСТЕРА СДЕЛАНА ОТДЕЛЬНЫМ ЭКРАНОМ.
 *
 * Форма администратора ищет по базе от 4–5 цифр — это перебор клиентской базы по
 * номерам, то есть медленная выгрузка (ТЗ §2.7.1.1, GAPS E3, ADR-024). Право записывать,
 * выданное мастеру решением владельца 44, этой границы не отменяет. Поэтому запрос
 * УХОДИТ ТОЛЬКО С ПОЛНЫМ НОМЕРОМ: неполный ввод не даёт ни одного обращения к серверу,
 * а значит и достать из формы нечего — ни списка, ни одной чужой фамилии.
 *
 * Проверять это на стороне сервера нечем: GET /v1/cabinet/clients ищет по части номера
 * и по имени, и другого маршрута сегодня нет (в отчёте это названо пробелом — нужен
 * POST /v1/cabinet/clients/lookup-by-phone). Значит, единственное место, где граница
 * держится, — вот эта функция, и сломать её молча легче всего: достаточно смягчить
 * условие полноты. Отсюда её собственный тест.
 */

/** Только цифры введённого. «+7 (916) 123-45-67» → «79161234567». */
export function digitsOf(input: string): string {
  return input.replaceAll(/\D/gu, "")
}

/**
 * Российский номер целиком — 11 цифр: «8 916…», «+7 916…», «7 916…» и «916…»
 * (десять цифр, как их диктуют вслух) приводятся к одному виду.
 *
 * ПОЧЕМУ ТОЛЬКО РОССИЯ РАЗБИРАЕТСЯ ТАК ПОДРОБНО. Полнота номера — это правило страны,
 * а не общее правило: в E.164 от восьми до пятнадцати цифр, и «полон ли номер» в общем
 * случае не определено. Салоны сегодня российские (два домена в белом списке
 * регистратора), поэтому разобран российский случай, а номер, набранный с «+» и другим
 * кодом страны, принимается как есть — но только если в нём не меньше одиннадцати цифр.
 * Это осознанная граница, а не забытая строка.
 */
export function normalizePhone(input: string): string | null {
  const raw = input.trim()
  if (raw === "") return null

  const digits = digitsOf(raw)
  if (digits.length === 0) return null

  // Явный код страны, отличный от российского: разбирать его нечем, принимаем как есть.
  if (raw.startsWith("+") && !digits.startsWith("7")) {
    return digits.length >= 11 && digits.length <= 15 ? `+${digits}` : null
  }

  if (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))) {
    return `+7${digits.slice(1)}`
  }
  // Десять цифр без кода страны: так номер диктуют вслух, и так его набирает мастер.
  if (digits.length === 10 && digits.startsWith("9")) {
    return `+7${digits}`
  }

  return null
}

/** Введён ли номер целиком. Ровно на этом ответе стоит запрет искать по части номера. */
export function isCompletePhone(input: string): boolean {
  return normalizePhone(input) !== null
}

/**
 * Номер для чтения: «+7 916 123-45-67». Группировка российская; у чужого кода страны
 * номер остаётся в E.164 — придуманная группировка чужого номера мешает сверить его
 * с бумажкой, а не помогает.
 */
export function formatPhone(e164: string): string {
  const digits = digitsOf(e164)
  if (!e164.startsWith("+7") || digits.length !== 11) return e164
  const a = digits.slice(1, 4)
  const b = digits.slice(4, 7)
  const c = digits.slice(7, 9)
  const d = digits.slice(9, 11)
  return `+7 ${a} ${b}-${c}-${d}`
}

/**
 * Совпадает ли маска сервера с введённым номером.
 *
 * Нужна для одной проверки: сервер ищет по подстроке, и на полный номер он в принципе
 * способен вернуть карточку, у которой совпал не весь номер, а его хвост в другом поле.
 * Сравниваются последние две цифры и код — больше в маске «+7 916 ••• •• 67» ничего нет.
 */
export function maskMatchesPhone(masked: string | null, e164: string): boolean {
  if (masked === null) return false
  const maskedDigits = digitsOf(masked)
  const phoneDigits = digitsOf(e164)
  if (maskedDigits.length === 0 || phoneDigits.length < 2) return false
  return phoneDigits.endsWith(maskedDigits.slice(-2))
}
